/**
 * BlinkPages site analytics report — Alpine.js component.
 *
 * Holds the page's payloads and decides what every chart card shows. One component rather than
 * one per card, because the figures share a payload bucket and a single live region — and because
 * a toggle on the trend has to repaint it without a server round trip, which means the payloads
 * have to live somewhere the toggles can reach.
 *
 * Every card is drawn by the shared Superset bundle through <x-charts.superset-chart>, which owns
 * its canvas, its accessible name and table and its loading, empty and failed boxes, and waits for
 * the viewport itself. This component hands each host a contract assembled from the held payloads
 * (charts/site-analytics-chart-contract.js) with the page's own tooltip, or a status: the LINE
 * cards — the trend, the branded and AI splits and the series an expanded Explore row opens to —
 * as a line or a two-axis chart, the channel donut as a pie and the "Break down by" bars as a bar
 * chart over the breakdown's categories.
 *
 * The chart modules are dynamically imported: this module is reachable from app.js, the single
 * Vite entry on every layout, so importing them statically would ship them on the login screen.
 *
 * Prefix: sar_
 */

/** The shapes a card can draw. A card declares one; the id only has to be unique. */
const SHAPES = ['trend', 'donut', 'bars', 'row', 'split'];

/** The line shapes, whose payloads are daily series; the donut and the bars draw a breakdown. */
const SERIES_SHAPES = ['trend', 'row', 'split'];

/**
 * The datasets a split card draws: BrandedSearchSplit::BRANDED_DATASET and the AI assistant half
 * of the source/medium table. Their payloads name one of these in `query.dataset`, which is how a
 * split card finds its own and the trend leaves them all out: by content, never by the page's key.
 * No measure of the trend resolves to any of them (the resolver never picks a segmented dataset),
 * so a series payload naming one can only be a split card's.
 *
 * Kept in step with the PHP by SiteAnalyticsChartModulesTest, which reads this list out of this
 * file and compares it with the segmented datasets the declarations carry: a twin added in PHP
 * and forgotten here would be drawn on the trend instead, with nothing failing.
 */
const SPLIT_DATASETS = ['branded_queries', 'ai_assistants'];

/**
 * The key figures' slot (Analytics::SLOT_TILES): their totals, which the page draws in PHP and no chart
 * here ever does (S46). Left out by key rather than by content, so a figure's payload can never be
 * taken for one a chart draws, whatever shape a later figure reads in. Kept in step with the PHP by
 * SiteAnalyticsChartModulesTest.
 */
const FIGURE_SLOT = 'tiles.';

/**
 * The legend key of Traffic over time's period before (D110 of documentation/pr-plans/insights-simplify-plan.md):
 * the one series the reader's trend draws, over the window the key figures' changes compare against, drawn
 * dashed beside it. No series key can be it, since every one is "<source>.<measure>". Its payloads
 * (Analytics::SLOT_BEFORE) come on an event of their own, site-analytics-before, and are held apart from
 * `payloads`, so no card ever selects one by content as this window's.
 */
const BEFORE_KEY = 'before';

/** The period before's line, as the bundle's seriesStyles.lineType draws it. */
const BEFORE_LINE = 'dashed';

/**
 * The split cards that draw their left axis's series as a stack (lineChart()'s `stack`): the
 * branded card, whose two clicks halves then reach the site's search clicks at their top edge.
 */
const STACKED_DATASETS = ['branded_queries'];

/**
 * The toggle keys a split card opens with switched off, by the dataset it draws: the branded card
 * opens on clicks, with its impressions one legend press away (D10 in
 * documentation/pr-plans/site-insights-answers-root-1.md), because impressions are noisy and an
 * order of magnitude above the clicks. Seeded once per card, so a reader's own presses stand.
 * SiteAnalyticsChartModulesTest holds the keys to the ones BrandedSplitPayloadBuilder writes.
 */
const DEFAULT_HIDDEN = {
    branded_queries: ['google_search_console.branded_impressions', 'google_search_console.non_branded_impressions'],
};

/** The event <x-charts.superset-chart> listens on; see resources/js/alpine/superset-chart.js. */
const HOST_EVENT = 'superset-chart-data';

/**
 * A card's host id: the card's id, prefixed so it can never collide with another surface's host on
 * the page. chart.blade.php renders the host under the same spelling, which
 * SiteAnalyticsChartModulesTest holds the two to.
 */
const hostIdOf = (id) => `sa-${id}`;

/**
 * A detail as the host would compare it: its status, reason and contract. The tooltip and a
 * donut's share formatter are functions and cannot be compared, and need no comparing: they read
 * the same payloads the contract is built from, so an unchanged contract carries unchanged ones.
 */
const sentKey = (detail) => JSON.stringify({ ...detail, tooltip: undefined, formatShare: undefined });

const isBreakdown = (payload) => payload?.kind === 'breakdown';

const isSeries = (payload) => payload?.kind === 'series';

const isSplit = (payload) => isSeries(payload) && SPLIT_DATASETS.includes(payload?.query?.dataset);

/**
 * A split card's own payload: the one naming the dataset it was pinned to. A card with no pin draws
 * nothing, since every split payload names a dataset and none equals a missing pin: with two split
 * datasets on the page, an unpinned card would otherwise take whichever split payload is held
 * first, which after the branded card's Retry is the AI assistant series.
 */
const isSplitFor = (payload, dataset) => isSplit(payload) && payload?.query?.dataset === dataset;

const isTrendSeries = (payload) => isSeries(payload) && !isSplit(payload);

/** The interval a series payload's rows are grouped by (Query::toArray()'s `interval`), as mergeTrendSeries() reads it. */
const intervalOf = (payload) => payload?.query?.interval ?? 'daily';

/**
 * The strings this component may render, with their English fallbacks.
 *
 * An explicit allowlist, like the metric screen's msc_text: a key a caller passes that this
 * omits is dropped before it can reach a chart, and the hardcoded English below is used
 * forever — no error, no warning, and the string still renders, just untranslated. Stating
 * the keys in one place is what makes that miss findable.
 */
const allowText = (bag = {}) => ({
    loading: bag.loading ?? 'Loading…',
    noData: bag.noData ?? 'No data for the selected period.',
    allHidden: bag.allHidden ?? 'Every series on this chart is hidden.',
    nothingAtInterval: bag.nothingAtInterval ?? 'None of the series switched on here can be drawn at this interval.',
    legendGroup: bag.legendGroup ?? 'Series shown',
    noDataPeriod: bag.noDataPeriod ?? 'No data for this period',
    emptyReasons: bag.emptyReasons ?? {},
    seriesShown: bag.seriesShown ?? ':series shown',
    seriesHidden: bag.seriesHidden ?? ':series hidden',
    dataThrough: bag.dataThrough ?? ':source data through :date',
    seriesPill: bag.seriesPill ?? ':series · :source',
    periodBefore: bag.periodBefore ?? 'The period before',
    sources: bag.sources ?? {},
    dimensions: bag.dimensions ?? {},
    reasons: bag.reasons ?? {},
});

/**
 * @param {object} config
 * @param {object} config.payloads       Payloads the server already had, keyed as the page
 *                                       keys them — the same map `site-analytics-data` sends.
 * @param {string[]} config.enabledSeries Trend toggle keys on first paint
 *                                       (ReportDefinition::defaultTrendSeries()).
 * @param {object} config.text           Translated strings, from the same __() bag the
 *                                       markup uses. Threaded in rather than hardcoded: every
 *                                       string this component announces has a visible twin
 *                                       rendered by Blade, and English here would leave
 *                                       screen-reader and localised users with English while
 *                                       the page around them translated.
 * @param {object} config.formats        Unit -> number-format descriptor the line cards print
 *                                       their axes and tables with, from
 *                                       SiteAnalyticsFormatter::chartFormats() in the site's
 *                                       notation. Static for the page, so safe in x-data.
 */
export default ({ payloads = {}, enabledSeries = [], text = {}, formats = {}, soloSeries = [] } = {}) => {
    // Render batching. Every card carries the window listeners, so one dispatch reaches the
    // component once per card on the page; without this, three cards would each assemble and
    // compare their contracts three times for nothing. The merge itself is idempotent, so only
    // the DRAW is deferred — to the end of the same tick, before the browser paints.
    let queued = false;
    const pending = new Set();

    // The last detail each card's host was sent, as sentKey() spells it. Every data event
    // re-renders every card, and a host handed a contract re-mounts its chart, replaying the
    // entry animation; a card whose contract did not change is not sent it again.
    const sent = new Map();

    return {
        /** Mounted cards, id -> { id, shape, dimension, dataset, seriesAction }. */
        cards: {},

        payloads: { ...payloads },
        rowPayloads: {},

        /**
         * Traffic over time's period before (BEFORE_KEY): its payloads by the page's key, and the name
         * its line takes, which the page sends with them because This year is compared with the same
         * days last year.
         */
        before: {},
        beforeLabel: null,
        enabledSeries: [...enabledSeries],

        /**
         * The series drawn only alone (ReportDefinition::soloSeriesKeys(), Avg. position): a line card
         * prints one unit an axis, so one beside a count would take the chart down.
         */
        soloSeries: [...soloSeries],

        /**
         * The switched-on series the trend can draw at the page's interval, or null until the page
         * says. Off daily a per-day-only series (Visitors) stays switched on but cannot be drawn,
         * and a trend left with nothing to draw runs no query: see sar_intervalLeavesNothing().
         */
        drawnSeries: null,

        /**
         * The payload keys the MOST RECENT event carried, per kind.
         *
         * `payloads` is history — it is merged by key so a slot the page did not re-run keeps
         * showing its figure. But a filter change MOVES a measure to another dataset (sessions
         * read `overview` unfiltered and `source_medium` under a Source filter), and the
         * superseded payload keeps its own key, so drawing everything in the map would put a
         * stale series beside its replacement. A figure therefore draws what the last event
         * said it draws, and falls back to the map only when that event carried nothing of
         * its kind.
         */
        current: { series: [], breakdown: [], split: [] },

        /**
         * Chart id -> its legend entries, as of the last contract this card built.
         *
         * Held rather than derived on demand because the chart modules are a dynamic import
         * (they must not sit in the entry chunk) and a Blade `x-for` cannot await one. Every
         * entry the card OFFERS is here, hidden ones included — a legend that dropped the
         * entry it just switched off would leave the reader no way back.
         */
        legends: {},

        /**
         * Chart id -> {label: colour} its host reported drawing. The donut draws in the
         * workspace's dashboard palette, which only the bundle resolves, so its legend's swatches
         * are these. Kept per label across draws: a hidden wedge is drawn no longer, and when it is
         * switched back on it keeps the colour it had.
         */
        swatches: {},

        /**
         * Chart id -> the legend keys the reader switched off, for every shape but the trend.
         *
         * A trend keeps no hidden set of its own: its series are the page's `enabledSeries`,
         * which the server-rendered pills also press, so one state is the only way the two can
         * never contradict each other. Everything else — the donut, the bars, the branded
         * split, an expanded Explore row — has no pills and holds its hidden keys here, which
         * is what survives a redraw of the same payload.
         */
        hidden: {},

        renderTokens: {},

        /**
         * Chart card id -> its Interval switch as the server last settled it: `selected`, the
         * interval the card is drawn at ('weekly'), and `options`, what the period offers
         * ([{value, label}], finest first). Handed over by the page on every render (its keyed sync
         * element); a card draws only the payloads at its `selected`.
         */
        intervals: {},

        /**
         * The open Explore row's switch, from its latest answer. Held apart from `intervals`
         * because the answer the row's chart mounts with arrives before that chart registers as a
         * card, and a row chart's scope draws one row.
         */
        rowInterval: null,

        /**
         * Chart card id -> the interval its switch was just pressed to, until the press is
         * answered. Shown over `intervals`, so the switch moves with the click rather than a round
         * trip later, and kept apart from it: Livewire answers one component's calls in order, so a
         * render asked for BEFORE the pick can land after it without flipping the switch back.
         */
        picks: {},

        liveMessage: '',

        sar_text: allowText(text),

        /**
         * Register a card and hand its host its first detail.
         *
         * Called from each card's own x-init rather than looked up from here, so the
         * component never has to know which cards the page chose to render — the Explore
         * row's chart exists only while a row is open. The host waits for the viewport itself
         * (its `lazy` prop), and decides an empty card before the bundle is ever fetched.
         */
        sar_mount(id, shape, dimension = null, cardText = null, options = {}) {
            if (!SHAPES.includes(shape)) {
                console.error(`[site analytics] card "${id}" declares the unknown shape "${shape}".`);
                return;
            }

            // The card's strings sit UNDER anything the page passed explicitly, so a page that
            // wants its own wording keeps it while a page that passes none still gets the
            // card's translated bag rather than the English fallbacks above.
            if (cardText) this.sar_text = allowText({ ...cardText, ...text });

            this.cards = {
                ...this.cards,
                // `seriesAction` names the Livewire method this card's legend presses instead of
                // toggling locally — the reader-driven trend passes `toggleSeries`, the same
                // method its pills call, so pressing either moves the one server-held set.
                [id]: {
                    id,
                    shape,
                    dimension: dimension || null,
                    // Which split this card draws. Two split cards on one page ask two different
                    // questions, so one with no pin draws nothing (see isSplitFor).
                    dataset: options.dataset || null,
                    seriesAction: options.seriesAction || null,
                },
            };

            if (shape === 'split' && DEFAULT_HIDDEN[options.dataset] && !(id in this.hidden)) {
                this.hidden = { ...this.hidden, [id]: [...DEFAULT_HIDDEN[options.dataset]] };
            }

            this.sar_render(id);
        },

        /** Forget a card that is being removed (an Explore row collapsing). */
        sar_unmount(id) {
            this.cards = this.sar_without(this.cards, id);
            this.legends = this.sar_without(this.legends, id);
            this.swatches = this.sar_without(this.swatches, id);
            this.hidden = this.sar_without(this.hidden, id);
        },

        destroy() {
            // An Explore row's host is torn down with this scope, and the next row opened under
            // the same id is replayed the last detail its id was sent — which must not be the
            // lines of the row that just closed.
            Object.values(this.cards).forEach((card) => this.sar_toHost(card.id, { status: 'loading' }));
        },

        /**
         * A `site-analytics-data` event: merge its payloads into the map by key, and record
         * which keys are current for each kind it carried. See `current` above for why both.
         */
        sar_ingest(detail = {}) {
            const incoming = Object.fromEntries(Object.entries(detail.payloads || {}).filter(([key]) => !key.startsWith(FIGURE_SLOT)));
            const keys = Object.keys(incoming);
            if (keys.length === 0) return;

            const merged = { ...this.payloads, ...incoming };
            this.payloads = merged;

            const current = { ...this.current };
            // The split is tracked apart from the trend's series: the branded card's own Retry
            // reloads only its slots, and an event carrying nothing but the split must not tell
            // the trend that no series of its own is current.
            Object.entries({ series: isTrendSeries, breakdown: isBreakdown, split: isSplit }).forEach(([kind, matches]) => {
                const arrived = keys.filter((key) => matches(incoming[key]));
                // Recomputed from every payload HELD, not from the keys this one event carried.
                // The page fetches one group per slot and source and answers each in its own
                // event, so taking the event's keys alone meant the second group to land erased
                // the first: a trend fed by Google Analytics AND Search Console drew only
                // whichever answered last, with the other's pill still pressed over a line that
                // was never there. Dropped payloads still prune this list, in sar_pending below.
                if (arrived.length > 0) {
                    current[kind] = Object.keys(merged).filter((key) => matches(merged[key]));
                }
            });
            this.current = current;

            // A refreshed slot is a fresh attempt: every card but the Explore row is re-sent, so a
            // host that failed to draw the previous payload is handed the one that has arrived.
            this.sar_schedule('data');
        },

        /**
         * A `site-analytics-loading` event: the page has claimed these slot keys and is about to
         * fetch them.
         *
         * Dropping their payloads is the point of the event. `payloads` is history, so a card
         * whose slot is being re-run still holds the PREVIOUS window's series, and sar_state()
         * would keep reporting 'chart' over it — the reader looking at last month's line under
         * this month's chip until the answer landed. With them gone every affected card is
         * 'loading' in the same frame as the click, and each fills again as its own group
         * answers.
         *
         * An empty key list means "everything", which is what a whole-report reload sends.
         */
        sar_pending(detail = {}) {
            const keys = Array.isArray(detail.keys) ? detail.keys : [];
            const dropped = keys.length === 0 ? Object.keys(this.payloads) : keys;
            if (dropped.length === 0) return;

            const payloads = { ...this.payloads };
            dropped.forEach((key) => delete payloads[key]);
            this.payloads = payloads;

            // The period before is re-run with the slots it sits beside, and a line of the window the
            // reader just left must not stay drawn under the new one.
            const before = { ...this.before };
            (keys.length === 0 ? Object.keys(before) : keys).forEach((key) => delete before[key]);
            this.before = before;

            // `current` names the keys a figure draws; one being re-run draws nothing until it
            // is back, and leaving it listed would send sar_payloadsFor() to a payload that is
            // no longer in the map.
            this.current = Object.fromEntries(Object.entries(this.current)
                .map(([kind, kept]) => [kind, kept.filter((key) => !dropped.includes(key))]));

            this.liveMessage = this.sar_text.loading;
            this.sar_schedule('data');
        },

        /**
         * A `site-analytics-row` event: the series an Explore row expanded to, and the row's switch.
         *
         * An errored payload beside a drawable one is dropped, as the Explore card's
         * rowChartPayloads() drops it on mount. The row's own switch re-asks a row whose chart is
         * showing, so that answer reaches this listener rather than a fresh mount, and one failed
         * source would otherwise turn the whole chart into a Retry that re-runs the page's slots,
         * not this row.
         */
        sar_ingestRow(detail = {}) {
            const payloads = detail.payloads || {};
            const drawable = Object.fromEntries(Object.entries(payloads).filter(([, payload]) => !payload?.error));

            this.rowPayloads = Object.keys(drawable).length > 0 ? drawable : { ...payloads };

            if (detail.interval && typeof detail.interval === 'object') {
                this.rowInterval = detail.interval;
            }

            this.sar_schedule('row');
        },

        /**
         * A `site-analytics-before` event: Traffic over time's period before (BEFORE_KEY), merged by key
         * as `payloads` is, so each source's answer adds to the other's. Only the trend draws it.
         */
        sar_ingestBefore(detail = {}) {
            const incoming = detail.payloads || {};
            if (Object.keys(incoming).length === 0) return;

            this.before = { ...this.before, ...incoming };
            if (typeof detail.label === 'string' && detail.label !== '') this.beforeLabel = detail.label;

            this.sar_schedule('trend');
        },

        /** The page's sync element: every chart card's switch, as this render settled it. */
        sar_takeIntervals(intervals) {
            if (!intervals || typeof intervals !== 'object' || Array.isArray(intervals)) return;

            this.intervals = { ...this.intervals, ...intervals };
            this.sar_schedule('data');
        },

        sar_intervalState(id) {
            return this.cards[id]?.shape === 'row' ? this.rowInterval : this.intervals[id];
        },

        /** The interval a chart card draws, or null for a card the page named none for. */
        sar_intervalOf(id) {
            return this.picks[id] ?? this.sar_intervalState(id)?.selected ?? null;
        },

        /** What the card's Interval switch offers, for <x-charts.interval-switch>. */
        sar_intervalOptions(id) {
            return this.sar_intervalState(id)?.options ?? [];
        },

        /**
         * A chart card's Interval switch was picked. The switch shows the pick at once, and the card
         * drops to loading until rows at that interval land: a page card's from the page
         * (setCardInterval), an Explore row's from the Explore card, which owns the row's question
         * and asks it again. An interval the switch does not offer, or the one already drawn,
         * changes nothing.
         */
        sar_pickInterval(id, value) {
            const card = this.cards[id];
            const offered = this.sar_intervalOptions(id).some((option) => option.value === value);

            if (!card || !offered || value === this.sar_intervalOf(id)) return;

            if (card.shape === 'row') {
                this.rowInterval = { ...this.rowInterval, selected: value };
                this.rowPayloads = {};
                this.sar_schedule('row');
                // From this scope's own root, which is the row's chart inside the Explore card, so it
                // bubbles to the card that re-asks the row. The page keeps no element of its own per card.
                this.$dispatch('site-analytics-row-interval', { interval: value });

                return;
            }

            this.picks = { ...this.picks, [id]: value };
            this.sar_schedule('data');

            // Dropped only once the answer's own render has handed the switches over: Livewire
            // resolves the call BEFORE it morphs (the morph waits two microtasks), and Alpine
            // initialises the new sync element after that, so dropping the pick on resolve would
            // show the switch as it was for that gap.
            const settle = () => setTimeout(() => {
                this.picks = this.sar_without(this.picks, id);
                this.sar_schedule('data');
            }, 0);

            // Outside Livewire (a preview page, a static embed) there is no server to ask; the
            // guard is sar_reload()'s, for the reason given there.
            try {
                Promise.resolve(this.$wire.setCardInterval(id, value))
                    .catch((error) => console.error('Failed to change the chart interval:', error))
                    .finally(settle);
            } catch (error) {
                settle();
                console.debug('Site analytics interval switch found no Livewire component to ask:', error);
            }
        },

        /**
         * Drop the errored payloads the pressed card draws, so its box leaves 'failed' now rather
         * than a network round trip later.
         *
         * sar_state() reports 'failed' for as long as the card holds an errored payload, so
         * without this a card whose answer failed kept its failure box — and the Retry button
         * inside it — until sar_ingest() landed a replacement: nothing visibly happened on the
         * press, and focus had already left a button that was still sitting there. Dropping the
         * payload is the same move retry(grain) already makes on the Explore card.
         *
         * Compared by identity: sar_payloadsFor() reads these very objects out of the same map
         * this filters, and Alpine hands back one cached proxy per raw object, so the entries and
         * the selection are the same references either side of the proxy.
         */
        sar_dropErrored(id) {
            const card = this.cards[id];

            if (!card) return;

            const errored = this.sar_payloadsFor(id).filter((payload) => payload.error);

            if (errored.length === 0) return;

            const isRow = card.shape === 'row';
            const source = isRow ? this.rowPayloads : this.payloads;
            const kept = Object.fromEntries(
                Object.entries(source).filter(([, payload]) => !errored.includes(payload)),
            );

            if (isRow) {
                this.rowPayloads = kept;
            } else {
                this.payloads = kept;
            }
        },

        /** A copy of `subject` without `key` — the object equivalent of dropping one card. */
        sar_without(subject, key) {
            const { [key]: dropped, ...rest } = subject;

            return rest;
        },

        /**
         * The payloads a card draws.
         *
         * Selected by CONTENT, never by key: the page owns its own key strings (the contract
         * says only that they are the ones it passed to runMany), so a card that matched on
         * them would break the first time the page renamed one.
         */
        sar_payloadsFor(id) {
            const card = this.cards[id];
            if (!card) return [];

            if (card.shape === 'row') {
                return Object.values(this.rowPayloads).filter(isSeries);
            }

            // A line card draws only rows at its own interval: two trend cards at two intervals each
            // find their own, and a card whose switch was just pressed shows loading rather than
            // the interval it left. While a press is out it draws nothing at all, because rows held
            // from an earlier visit to that interval may predate a search type change, which
            // re-runs only the keys in use; the press's own answer claims and refetches them. A
            // card the page named no interval for draws what it holds, as every card did before.
            if ((card.shape === 'trend' || card.shape === 'split') && id in this.picks) return [];

            const interval = this.sar_intervalOf(id);
            const atInterval = (payload) => interval === null || intervalOf(payload) === interval;

            if (card.shape === 'trend') {
                const isOwn = (payload) => isTrendSeries(payload) && atInterval(payload);
                const current = this.current.series.map((key) => this.payloads[key]).filter(isOwn);

                return current.length > 0 ? current : Object.values(this.payloads).filter(isOwn);
            }

            // One payload: a split card asks one question per load, so the newest answer naming
            // ITS dataset is the one it draws. The pin is what keeps two split cards apart.
            if (card.shape === 'split') {
                const matches = (payload) => isSplitFor(payload, card.dataset) && atInterval(payload);
                const current = this.current.split.map((key) => this.payloads[key]).find(matches);
                const payload = current ?? Object.values(this.payloads).find(matches);

                return payload ? [payload] : [];
            }

            const payload = this.sar_breakdownFor(card);

            return payload ? [payload] : [];
        },

        /**
         * The payloads a card is actually DRAWING from, which is not the same as what it holds.
         *
         * The reader's trend asks for every series it offers so that a tick needs no fetch, so
         * it is handed payloads for sources nobody ticked. News belonging to one of those — a
         * statement that failed, an empty answer, a freshness line, a filter that could not be
         * honoured — is not this chart's news, and printing it means describing data that is
         * not on screen: a retry box over lines that loaded, or "Search Console runs 2 days
         * behind" under a chart with no Search Console line on it.
         *
         * Only the reader's trend narrows. A pinned trend, an Explore row and the branded split
         * draw everything they hold, and `card.seriesAction` is what tells the two apart.
         */
        sar_drawnPayloadsFor(id) {
            const card = this.cards[id];
            const payloads = this.sar_payloadsFor(id);

            if (!card || card.shape !== 'trend' || !card.seriesAction) return payloads;

            // `query` is on every payload including an errored or a state one: PayloadEnvelope
            // builds it from the query before withError()/withState() ever run.
            return payloads.filter((payload) => (payload.query?.measures || [])
                .some((measure) => this.enabledSeries.includes(`${payload.query.source}.${measure}`)));
        },

        /**
         * Which breakdown payload a donut or a bars card draws.
         *
         * A card PINNED to a dimension (the donut, whose dimension is fixed by
         * ReportDefinition::DONUT) takes that dimension wherever it can find it, including
         * from the history — otherwise refreshing only the "Break down by" panel would blank
         * the donut beside it. An UNPINNED card follows the reader's own selection: the most
         * recent breakdown no pinned card has claimed, and failing that the most recent one
         * at all, which is what the reader sees after choosing the donut's own dimension.
         */
        sar_breakdownFor(card) {
            const current = this.current.breakdown.map((key) => this.payloads[key]).filter(isBreakdown);
            const all = Object.values(this.payloads).filter(isBreakdown);

            if (card.dimension) {
                return current.find((payload) => payload.dimension?.key === card.dimension)
                    ?? all.find((payload) => payload.dimension?.key === card.dimension)
                    ?? null;
            }

            const pinned = Object.values(this.cards).map((other) => other.dimension).filter(Boolean);

            return current.find((payload) => !pinned.includes(payload.dimension?.key))
                ?? current[0]
                ?? all.find((payload) => !pinned.includes(payload.dimension?.key))
                ?? null;
        },

        /**
         * One of four states, decided ONLY by the client-held payload.
         *
         * The four boxes swap for one another inside the chart host's wire:ignore'd root, so
         * anything Blade decided server-side would never be morphed and would keep describing the
         * previous window — the exact defect the metric screen's no-data box was moved
         * client-side to fix. The host is told the state; see sar_send().
         */
        sar_state(id) {
            const payloads = this.sar_drawnPayloadsFor(id);

            if (payloads.some((payload) => payload.error)) return 'failed';

            // A trend with every series switched off draws its empty grid, and BOTH ways of
            // reaching that set have to look the same: a fresh load of ?series[0]=none and
            // switching the last pill off in place. Gating this on `payloads.length === 0` only
            // covered the first, so the second fell through to sar_allHidden() and returned
            // 'empty' — the canvas was cleared and never redrawn, leaving the card on whatever
            // it drew last until something else forced a render.
            //
            // It sits ABOVE the two payload checks rather than below them because the drawn set
            // is empty here by definition, and an all-off card is a resting state rather than a
            // chart that failed or came back empty. The page does run its trend query for an
            // empty set now — that is what makes ticking one back on instant — so this also
            // covers the moment before the keyed sync element has handed the set over.
            if (this.cards[id]?.shape === 'trend' && this.enabledSeries.length === 0) return 'chart';

            // The same promise for a trend whose switched-on series the interval cannot draw
            // (Visitors alone, grouped by week): the page runs no trend query for it either.
            if (this.cards[id]?.shape === 'trend' && this.sar_intervalLeavesNothing(id)) return 'empty';
            if (payloads.length === 0) return 'loading';
            if (!payloads.some((payload) => payload.hasData)) return 'empty';

            // Every entry switched off. The empty box says so in words rather than leaving a
            // grid with nothing on it, which reads as a chart that failed to load — and it is
            // never the skeleton, because the data is here and nothing further is coming.
            if (this.sar_allHidden(id)) return 'empty';

            return 'chart';
        },

        /**
         * Whether series are switched on and the card's interval can draw none of them. Only an
         * explicit empty set from the page counts: null is a page that has not said, which is every
         * page at daily before its first sync. A card with its own switch is judged by the set the
         * page worked out at that card's interval, since a card picked back to daily draws Visitors
         * under a weekly page; and a card whose press is still out is loading, not empty, because
         * nothing has said yet what its new interval can draw.
         */
        sar_intervalLeavesNothing(id) {
            if (id in this.picks) return false;

            const drawn = this.intervals[id]?.drawn ?? this.drawnSeries;

            return this.enabledSeries.length > 0 && Array.isArray(drawn) && drawn.length === 0;
        },

        /** Whether this card has a legend and the reader has switched every entry off. */
        sar_allHidden(id) {
            const entries = this.legends[id] || [];

            return entries.length > 0 && entries.every((entry) => this.sar_hiddenKeys(id).includes(entry.key));
        },

        /**
         * The legend keys this card is NOT drawing.
         *
         * Derived on every read rather than stored on the entries: an all-off card renders the
         * empty box instead of a canvas, so no draw happens to refresh a stored flag, and a
         * pressed-state frozen at "off" would leave the legend unable to describe the press
         * that turns one back on — the card would never leave the empty box.
         */
        sar_hiddenKeys(id) {
            const card = this.cards[id];
            if (!card) return [];

            const local = this.hidden[id] || [];

            // A trend's series are the page's set as well: a card whose legend presses the
            // pills' Livewire method has an empty local set and reads its hidden entries off
            // `enabledSeries`. Both are unioned here, so neither control can draw a line the
            // other switched off.
            //
            // A pinned trend has no pills and keeps its own local set, but it is subject to
            // `offPage` too — which is why sar_legendFor() never offers it an entry outside the
            // page's set: it could not press one back on.
            if (card.shape === 'trend') {
                // The period before is no series of the page's: its press is this card's own.
                const offPage = (this.legends[id] || [])
                    .map((entry) => entry.key)
                    .filter((key) => key !== BEFORE_KEY && !this.enabledSeries.includes(key));

                return [...new Set([...local, ...offPage])];
            }

            return local;
        },

        /**
         * This card's legend: every entry it offers, each with its own pressed state and the
         * colour its chart draws it in.
         *
         * A donut's colours are the workspace palette's, known only once its host reports a draw,
         * so its legend waits for that report rather than showing swatches its wedges do not have.
         * It never waits with every wedge switched off: nothing is drawn then, every swatch is the
         * hidden grey, and the buttons are the reader's only way back.
         */
        sar_legend(id) {
            const keys = this.sar_hiddenKeys(id);
            const swatches = this.swatches[id];

            if (this.cards[id]?.shape === 'donut' && !swatches && !this.sar_allHidden(id)) return [];

            return (this.legends[id] || []).map((entry) => ({
                ...entry,
                color: entry.color ?? swatches?.[entry.key] ?? null,
                hidden: keys.includes(entry.key),
            }));
        },

        /**
         * What a card's host reported drawing, `superset-chart-drawn` bubbling up to the card. Merged
         * over what it reported before, so a wedge switched off keeps its colour for its return.
         */
        sar_hostDrawn(id, detail = {}) {
            const names = Array.isArray(detail.names) ? detail.names : [];
            const colors = Array.isArray(detail.colors) ? detail.colors : [];
            const drawn = Object.fromEntries(names
                .map((name, index) => [name, colors[index]])
                .filter(([, color]) => typeof color === 'string' && color !== ''));

            this.swatches = { ...this.swatches, [id]: { ...(this.swatches[id] ?? {}), ...drawn } };
        },

        /**
         * Hide or restore one legend entry.
         *
         * A card that names a `seriesAction` moves the PAGE's set rather than a set of its own:
         * its entries are the same decision the pills carry, and two client copies of one reader
         * decision is exactly how a legend and a pill come to disagree.
         *
         * It flips that set here and draws before telling the server, the same order the pills
         * use, because the server is only being asked to remember the choice for `?series=` —
         * the payloads for every series are already held. Handing the press straight to Livewire
         * instead, as this did, left the canvas untouched until the answer came back and the
         * keyed sync element re-ran: the one control on the card that still felt like a query.
         *
         * sar_toggleSeries owns the announcement here, so this branch makes none of its own. Its
         * sentence names the source as well ("Visitors · Google Analytics"), which is the one
         * the pills already speak, and two controls over one decision should not say it two
         * different ways.
         *
         * Everything else is local in its own `hidden` set, for the same reason.
         */
        sar_toggleLegend(id, key) {
            const card = this.cards[id];
            if (!card) return;

            if (card.seriesAction && key !== BEFORE_KEY) {
                // Flip and draw FIRST, tell the server after. sar_toggleSeries moves the page's
                // set, announces the change and schedules the repaint; the Livewire call that
                // follows only records the choice for `?series=`.
                this.sar_toggleSeries(key);
                this.$wire[card.seriesAction](key);

                return;
            }

            const wasHidden = this.sar_hiddenKeys(id).includes(key);
            const entry = (this.legends[id] || []).find((candidate) => candidate.key === key);

            // The repaint is otherwise the only signal a sighted reader gets, and a screen
            // reader gets none at all.
            this.liveMessage = this.sar_fill(
                wasHidden ? this.sar_text.seriesShown : this.sar_text.seriesHidden,
                { ':series': entry?.label ?? key },
            );

            const hidden = this.hidden[id] || [];

            this.hidden = {
                ...this.hidden,
                [id]: wasHidden ? hidden.filter((candidate) => candidate !== key) : [...hidden, key],
            };

            this.sar_schedule(card.shape === 'row' ? 'row' : 'data');
        },

        /** The empty box's sentence: the payload's own reason where the bag names one. */
        sar_emptyMessage(id) {
            if (this.cards[id]?.shape === 'trend' && this.sar_intervalLeavesNothing(id)) return this.sar_text.nothingAtInterval;
            if (this.sar_allHidden(id)) return this.sar_text.allHidden;

            const payload = this.sar_drawnPayloadsFor(id).find((candidate) => !candidate.hasData);
            const reason = payload?.emptyReason;

            return this.sar_text.emptyReasons[reason] ?? this.sar_text.noData;
        },

        /**
         * Substitute :placeholders in a translated string, in ONE pass, with a function.
         *
         * The same shape as metric-screen-chart.js's msc_fill, and duplicated rather than
         * shared for the two reasons that make sharing it cost more than it saves: the
         * placeholder sets differ, and the only module below both components is in the entry
         * chunk on every page. Both properties below are load-bearing:
         *
         * - A FUNCTION replacement is never scanned for `$$`, `$&`, `` $` `` or `$'`, which
         *   String.prototype.replace interprets as substitution patterns when the replacement
         *   is a string. A page titled `/pricing?a=$'` would otherwise splice a copy of the
         *   rest of the template into its own accessible name.
         * - ONE pass means a substituted value can never be re-scanned, so a dimension value
         *   that literally contains ":count" cannot rewrite the next placeholder.
         *
         * Unknown tokens collapse to '' rather than printing the raw placeholder.
         */
        sar_fill(template, values) {
            return String(template ?? '').replace(/:series|:source|:count|:title|:date|:dimension/g, (token) => {
                const value = values[token];

                return value === undefined || value === null ? '' : String(value);
            });
        },

        /** The trend's pills: every series the payloads carry, with its on/off state. */
        get sar_seriesToggles() {
            const toggles = [];
            const seen = new Set();

            this.sar_payloadsFor('trend').forEach((payload) => {
                (payload.series || []).forEach((series) => {
                    // ReportDefinition::seriesKey()'s "<source>.<measure>", spelled here as well
                    // as in charts/site-analytics-options.js#seriesToggleKey because this
                    // component may not import that module statically — it would put the option
                    // builders in the entry chunk on every page. The two must agree: a mismatch
                    // silently draws nothing for a pill the reader just pressed.
                    const key = `${series.source}.${series.key}`;
                    if (seen.has(key)) return;

                    seen.add(key);
                    toggles.push({
                        key,
                        name: series.name,
                        sourceLabel: this.sar_text.sources[series.source] ?? series.source,
                        enabled: this.enabledSeries.includes(key),
                    });
                });
            });

            return toggles;
        },

        /** A pill's visible text: the series AND the source it came from, never the id. */
        sar_toggleLabel(toggle) {
            return this.sar_fill(this.sar_text.seriesPill, {
                ':series': toggle.name,
                ':source': toggle.sourceLabel,
            });
        },

        /**
         * What a pill press should make the enabled set, applied locally and returned.
         *
         * Returns the WHOLE new set for the server rather than the key that moved: a toggle is
         * computed from whatever state the request carried, so two presses inside one round trip
         * make the second one flip against a stale list and a series the reader never touched
         * turns itself back on. A set is idempotent whatever the order or the lag.
         *
         * BOTH directions are drawn here and now, from payloads already held: the page asks for
         * every series the trend offers whatever is ticked, so showing one is the same kind of
         * decision as hiding one. The server is told only so that `?series=` remembers it.
         */
        sar_pressSeries(key) {
            this.sar_toggleSeries(key);

            return [...this.enabledSeries];
        },

        /**
         * A key figure's press (D110 of documentation/pr-plans/insights-simplify-plan.md): Traffic over
         * time draws that figure alone, now, from the payloads it already holds, and the whole new set is
         * returned for `?series=`, as a pill's press returns it. Pressing the figure already drawn keeps
         * it: the figures pick what the one chart shows, so one is always picked.
         */
        sar_showSeries(key) {
            if (! this.sar_isShownAlone(key)) {
                this.enabledSeries = [key];

                const toggle = this.sar_seriesToggles.find((candidate) => candidate.key === key);
                this.liveMessage = this.sar_fill(this.sar_text.seriesShown, { ':series': toggle ? this.sar_toggleLabel(toggle) : key });

                this.sar_schedule('trend');
            }

            return [...this.enabledSeries];
        },

        /** Whether Traffic over time draws this series and nothing else: its figure reads as pressed. */
        sar_isShownAlone(key) {
            return this.enabledSeries.length === 1 && this.enabledSeries[0] === key;
        },

        /**
         * Add or remove a series, locally.
         *
         * No re-fetch: the trend's payloads already carry every series the toggles offer, so
         * the canvas repaints on the click. The change is otherwise conveyed by the repaint
         * alone, hence the announcement.
         */
        sar_toggleSeries(key) {
            const enabled = this.enabledSeries.includes(key);
            // A series drawn only alone is drawn alone when switched on, and switching on any other
            // takes it off, as Analytics::toggleSeries() does.
            const counts = this.enabledSeries.filter((candidate) => !this.soloSeries.includes(candidate));
            this.enabledSeries = enabled
                ? this.enabledSeries.filter((candidate) => candidate !== key)
                : (this.soloSeries.includes(key) ? [key] : [...counts, key]);

            const toggle = this.sar_seriesToggles.find((candidate) => candidate.key === key);
            this.liveMessage = this.sar_fill(
                enabled ? this.sar_text.seriesHidden : this.sar_text.seriesShown,
                { ':series': toggle ? this.sar_toggleLabel(toggle) : key },
            );

            this.sar_schedule('trend');
        },

        /**
         * A card's Retry, pressed inside its chart host. The host has already parked focus on this
         * card's heading (the root carries data-card) and dispatched `superset-chart-retry`,
         * because the status it was last sent was 'failed' and it holds no contract to redraw.
         */
        sar_hostRetry(id) {
            this.sar_reload(id);
        },

        /** Clear the pressed card's failure and ask the page to re-run its slots. */
        sar_reload(id) {
            this.sar_dropErrored(id);
            this.liveMessage = this.sar_text.loading;

            // The dropped payload leaves the card loading; its host is only told so by a render,
            // since its boxes are its own rather than bound to sar_state().
            const card = this.cards[id];
            if (card) this.sar_schedule(card.shape === 'row' ? 'row' : 'data');

            // `$wire` is an Alpine magic Livewire installs, and outside a Livewire component it
            // does not answer undefined — it throws "Could not find Livewire component in DOM
            // tree", as a String object with no stack, from the returned proxy's FIRST property
            // access. So `if (!this.$wire)` is not a guard, it is the throw: it passes (a proxy
            // is truthy) and the call after it is what lands in Alpine's handler, out of a click
            // handler, with nothing on screen to say so. Livewire exposes no way to ask
            // beforehand, so the call itself is the test — and these cards do render outside
            // Livewire (a preview page, a static embed), where Retry has nothing to ask.
            try {
                Promise.resolve(this.$wire.retry())
                    .catch((error) => console.error('Failed to reload the site analytics slots:', error));
            } catch (error) {
                console.debug('Site analytics Retry found no Livewire component to reload from:', error);
            }
        },

        /** "Google Analytics data through 13 Sep 2026", plus the lag sentence where there is one. */
        sar_dataThrough(id) {
            const lines = [];

            this.sar_drawnPayloadsFor(id).forEach((payload) => {
                Object.entries(payload.dataThrough || {}).forEach(([source, entry]) => {
                    if (!entry?.formatted) return;

                    const line = this.sar_fill(this.sar_text.dataThrough, {
                        ':source': this.sar_text.sources[source] ?? source,
                        ':date': entry.formatted,
                    });

                    if (!lines.includes(line)) lines.push(line);
                    if (entry.lagNote && !lines.includes(entry.lagNote)) lines.push(entry.lagNote);
                });
            });

            return lines;
        },

        /**
         * The filters this figure could NOT honour, said out loud beside it.
         *
         * Never silently dropped: a figure drawn under a filter chip that did not narrow it
         * is a wrong number presented as a right one, so the reason travels with the payload
         * and is printed here.
         */
        sar_notApplied(id) {
            const notes = [];

            this.sar_drawnPayloadsFor(id).forEach((payload) => {
                (payload.filters?.notApplied || []).forEach((entry) => {
                    // The sentence comes FORMATTED in the payload, because the page never formats
                    // and because composing it here used to fail open and silent: an entry whose
                    // reason key was missing from the string bag was dropped, so the one thing a
                    // reader needed to know about the number in front of them simply was not drawn.
                    // The bag is still read as a fallback for a payload cached before the sentence
                    // existed; a reason it does not know is now rendered rather than skipped.
                    const template = this.sar_text.reasons[entry.reason];
                    const note = entry.sentence
                        || (template
                            ? this.sar_fill(template, {
                                ':dimension': this.sar_text.dimensions[entry.dimension] ?? entry.dimension,
                            })
                            : null);

                    if (note && !notes.includes(note)) notes.push(note);
                });

                (payload.notes || []).forEach((note) => {
                    if (!notes.includes(note)) notes.push(note);
                });
            });

            return notes;
        },

        /**
         * Draw the cards a change touched, once, at the end of this tick.
         *
         * `data` is everything but the Explore row (the page's slots), `row` is the Explore
         * row alone, `trend` is a series toggle. A tick that saw more than one draws the
         * union, so a payload arriving in the same tick as a toggle still repaints once.
         */
        sar_schedule(kind) {
            pending.add(kind);

            if (queued) return;
            queued = true;

            queueMicrotask(() => {
                queued = false;

                const kinds = new Set(pending);
                pending.clear();

                Object.keys(this.cards)
                    .filter((id) => {
                        const shape = this.cards[id].shape;

                        if (kinds.has('data') && shape !== 'row') return true;
                        if (kinds.has('row') && shape === 'row') return true;

                        return kinds.has('trend') && shape === 'trend';
                    })
                    .forEach((id) => this.sar_render(id));
            });
        },

        sar_render(id) {
            const card = this.cards[id];
            if (!card) return;

            const token = (this.renderTokens[id] ?? 0) + 1;
            this.renderTokens = { ...this.renderTokens, [id]: token };

            const payloads = this.sar_payloadsFor(id);

            // The legend is rebuilt from THIS render's payloads before the state is decided, so a
            // card sitting in the all-hidden empty box is re-examined against the new data rather
            // than judged against the entries of the question before it — which is how an all-off
            // card would otherwise never come back when the reader changed filters.
            import('../charts/site-analytics-options')
                .then(async (options) => {
                    if (token !== this.renderTokens[id]) return;

                    this.legends = { ...this.legends, [id]: this.sar_legendFor(card, payloads, options) };

                    await this.sar_send(card, payloads, options, this.sar_state(id), token);
                })
                .catch((error) => {
                    console.error(`Failed to draw the "${id}" site analytics chart:`, error);

                    // The host announces its own failure; the page's region stays for the rest.
                    if (token === this.renderTokens[id]) this.sar_toHost(id, { status: 'failed' });
                });
        },

        /**
         * What a card's host is told: a status while there is nothing to draw, or the contract
         * and the page's own tooltip.
         *
         * The four states are still decided here, from the held payloads, because they are the
         * page's knowledge — a slot re-running, an errored answer, a source with no data, every
         * entry switched off. The host draws them and announces the changes, so the empty
         * sentence travels with the status: it is the card's reason, not the host's default.
         */
        async sar_send(card, payloads, options, state, token) {
            if (state === 'loading' || state === 'failed') {
                this.sar_toHost(card.id, { status: state });
                return;
            }

            if (state === 'empty') {
                this.sar_toHost(card.id, { status: 'empty', emptyMessage: this.sar_emptyMessage(card.id) });
                return;
            }

            const [chart, { tooltipPlacement }] = await Promise.all([
                import('../charts/site-analytics-chart-contract'),
                import('../charts/tooltip-placement'),
            ]);

            if (token !== this.renderTokens[card.id]) return;

            // Where the card's tooltip goes: in the window, beside the pointer, for this card's host.
            // Appended to the body, it is drawn above the sidebar; placed here, it never runs off the
            // window from a chart by its edge, which ECharts' own placement, knowing only the
            // chart's box, did. Made on the first hover, when ECharts asks: tooltipPlacement() reads
            // the document, which is only there in a browser.
            const hostId = hostIdOf(card.id);
            let placed = null;
            const placement = (...args) => (placed ??= tooltipPlacement(hostId))(...args);

            this.sar_toHost(card.id, SERIES_SHAPES.includes(card.shape)
                ? this.sar_lineDetail(card, payloads, options, chart, placement)
                : this.sar_breakdownDetail(card, payloads, options, chart, placement));
        },

        /**
         * A line card's detail: a line or a two-axis chart of its visible series, the branded card's
         * clicks stacked, and the tooltip printing the stack's total from the payload's total series.
         */
        sar_lineDetail(card, payloads, options, chart, placement = null) {
            const merged = options.mergeTrendSeries(payloads, card.shape === 'trend' ? this.enabledSeries : null);
            const before = this.sar_periodBefore(card, merged, options);
            const model = before ? { ...merged, series: [...merged.series, before] } : merged;
            const { contract, drawn, stacked } = chart.lineChart(model, {
                hidden: this.sar_hiddenKeys(card.id),
                formats,
                compact: card.shape === 'row',
                sourceLabels: this.sar_text.sources,
                stack: card.shape === 'split' && STACKED_DATASETS.includes(card.dataset),
            });

            // Every series switched off — in the legend, or the trend's last pill. Said in words,
            // as the empty box always has, rather than a bare grid that reads as a chart that
            // failed to load.
            if (!contract) return { status: 'empty', emptyMessage: this.sar_text.allHidden };

            return {
                contract,
                tooltip: options.seriesTooltip(drawn, model.dates, { noDataPeriod: this.sar_text.noDataPeriod }, {
                    total: stacked ? model.total : null,
                    placement,
                }),
                periods: chart.periodLabels(model),
            };
        },

        /**
         * Traffic over time's period before (D110, board 6a): the one series the reader's trend draws,
         * over the window the key figures' changes compare against, as a series of the model to draw
         * dashed in the same colour. Null on any other card, with more than one series drawn (a twin
         * for each would bury the lines it sits beside), and until the page has sent it at the card's
         * interval. Lined up by position, its first reading under this window's first, so a week's
         * shape sits under its own. A period before longer than this window (the whole month before a
         * month in progress) is drawn as far as this window's axis runs.
         */
        sar_periodBefore(card, model, options) {
            if (card.shape !== 'trend' || !card.seriesAction || model.series.length !== 1) return null;

            const [series] = model.series;
            const held = Object.values(this.before)
                .filter((payload) => !payload?.error && isTrendSeries(payload) && intervalOf(payload) === model.interval);
            const before = options.mergeTrendSeries(held, [series.key]).series[0];
            if (!before) return null;

            const values = model.dates.map((_, index) => before.values[index] ?? null);
            if (!values.some((value) => typeof value === 'number' && Number.isFinite(value))) return null;

            return {
                ...series,
                key: BEFORE_KEY,
                name: this.beforeLabel ?? this.sar_text.periodBefore,
                values,
                formatted: model.dates.map((_, index) => before.formatted[index] ?? null),
                lineType: BEFORE_LINE,
            };
        },

        /**
         * A donut's or a bars card's detail. A donut also hands the host its share rounding, so the
         * host's table reads each share exactly as the tooltip prints it.
         */
        sar_breakdownDetail(card, payloads, options, chart, placement = null) {
            const model = options.breakdownModel(payloads[0]);
            const isDonut = card.shape === 'donut';
            const draw = isDonut ? chart.donutChart : chart.barsChart;
            const { contract, drawn } = draw(model, { hidden: this.sar_hiddenKeys(card.id), formats });

            // Nothing drawn, and not because the reader hid it: sar_state() sends an all-hidden card
            // its empty box before any contract is built. What is left is a donut with no row that
            // is a share of anything — every row zero or below draws no wedge and lists no entry —
            // which would otherwise be a bare ring.
            if (!contract) return { status: 'empty', emptyMessage: this.sar_text.noData };

            const tooltip = options.breakdownTooltip(drawn, { measure: model.measure, shares: isDonut }, { noDataPeriod: this.sar_text.noDataPeriod }, { placement });

            return isDonut ? { contract, tooltip, formatShare: options.shareText } : { contract, tooltip };
        },

        /** Hand one card's host a detail, unless it is the one the host already has. */
        sar_toHost(id, detail) {
            const hostId = hostIdOf(id);
            const key = sentKey(detail);

            if (sent.get(hostId) === key) return;

            sent.set(hostId, key);
            window.dispatchEvent(new CustomEvent(HOST_EVENT, { detail: { id: hostId, ...detail } }));
        },

        /**
         * The entries this card's legend offers, hidden ones included.
         *
         * Built from the SAME pure helpers the chart's contract is assembled from — the merge a
         * line card draws, the breakdown's cells a donut or the bars draw — so a button can never
         * name an entry the chart is not drawing. A line or a bar carries the colour its contract
         * pins it to (categoricalColor() for a line); a wedge's comes from its host's report
         * (sar_legend). The reader's trend lists every series its payloads carry rather than only
         * the enabled ones — the pressed-off entries are the way back to them, and they are the
         * only place the colour of a switched-off line can be read.
         *
         * A PINNED trend does not, and the difference is not cosmetic. Its press falls to the
         * local `hidden` branch of sar_toggleLegend(), which cannot clear the keys
         * sar_hiddenKeys() derives from the page's `enabledSeries` — so an entry for a series
         * the reader has not ticked is a button that announces "shown" and then draws nothing.
         * Harmless while a trend only ever held the ticked series; now that it holds all of
         * them, the card would sprout three of them.
         */
        sar_legendFor(card, payloads, options) {
            if (SERIES_SHAPES.includes(card.shape)) {
                const enabled = card.shape === 'trend' && ! card.seriesAction ? this.enabledSeries : null;
                const entries = options.seriesLegend(options.mergeTrendSeries(payloads, enabled));
                // The period before's own entry, after the series it is drawn beside, listed whenever
                // it can be drawn, switched off included: its press is the reader's way back to it.
                const before = this.sar_periodBefore(card, options.mergeTrendSeries(payloads, card.shape === 'trend' ? this.enabledSeries : null), options);

                return before ? [...entries, { ...options.seriesLegend({ series: [before] })[0], dashed: true }] : entries;
            }

            const model = options.breakdownModel(payloads[0]);

            return card.shape === 'donut' ? options.donutLegend(model) : options.barsLegend(model);
        },
    };
};
